import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { LuPackageCheck, LuShieldCheck, LuSparkles } from 'react-icons/lu';

const sections = [
  {
    title: 'What Information We Collect',
    body:
      'When you place an order with Nithuja Cakes, we collect basic details such as your name, phone number, delivery location, order preferences, and any reference images you share. This helps us prepare your cake just the way you want it.'
  },
  {
    title: 'How We Use Your Information',
    body:
      'Your information is used only to confirm orders, coordinate delivery or pickup, communicate updates via WhatsApp or phone, and provide customer support. We never use your data for unrelated purposes.'
  },
  {
    title: 'Payments & Data Safety',
    body:
      'We primarily accept bank transfers and cash payments. Online payments may be available in limited cases. We do not store card or payment details on our website, and your personal data is handled with care.'
  },
  {
    title: 'Your Rights & Choices',
    body:
      'You are always in control of your information. If you wish to update or remove your personal details, you can contact us at any time and we will assist you promptly.'
  }
];

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy | Nithuja Cakes</title>
      <meta
        name="description"
        content="Privacy policy of Nithuja Cakes explaining how customer information is collected, used, and protected."
      />
    </Helmet>

    <div className="bg-gray-50 min-h-screen text-gray-800">
      <Navbar />

      <main className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-6 lg:px-8 xl:px-10">
        <p className="text-sm uppercase tracking-[0.35em] text-pink-600">Policies</p>

        <h1 className="mt-3 text-3xl sm:text-4xl font-serif font-bold text-gray-900">
          Privacy Policy
        </h1>

        <p className="mt-4 text-lg text-gray-600 leading-relaxed">
          At Nithuja Cakes, your trust matters to us. This Privacy Policy explains what information we collect,
          why we collect it, and how we keep it safe while serving you delicious, handcrafted cakes.
        </p>

        <section className="mt-10 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-pink-50 text-pink-600">
              <LuShieldCheck className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-pink-500">Protecting your details</p>
              <h2 className="mt-1 text-xl font-semibold text-gray-900">Careful handling from order to delivery</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                We only use your details to process orders, coordinate delivery or pickup, and provide customer support.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-8 grid gap-6">
          {sections.map(({ title, body }, index) => (
            <section
              key={title}
              className={`rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7 ${index === 0 ? 'border-l-4 border-pink-500' : index === 1 ? 'border-l-4 border-rose-400' : index === 2 ? 'border-l-4 border-amber-400' : 'border-l-4 border-emerald-400'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${index === 0 ? 'bg-pink-50 text-pink-600' : index === 1 ? 'bg-rose-50 text-rose-600' : index === 2 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                  <LuPackageCheck className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                  <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
                </div>
              </div>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-2xl bg-pink-50 border border-pink-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-pink-600 shadow-sm">
              <LuSparkles className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-pink-700">Contact Us</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">
                If you have any questions about this policy or your personal information, feel free to contact us at
                <a
                  href="mailto:contact.aarasolutions@gmail.com"
                  className="text-pink-600 font-semibold ml-1"
                >
                  contact.aarasolutions@gmail.com
                </a>
                or call <span className="font-semibold">+94 777 859 0241</span>.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
);

export default PrivacyPolicy;
